/**
 * Tide-aware shallow assessment and tidal-gate window computation.
 *
 * Charted soundings are depths BELOW chart datum. The tide sits on top of that,
 * so the real water over a spot at time t is (charted depth + height of tide
 * above datum at t). A patch that looks too shallow on the chart can be perfectly
 * crossable once the tide is up, and that is exactly the case this helper works
 * out.
 *
 * Given the charted depth samples along a shallow stretch, a function that
 * predicts the height of tide there at a time, our safety depth, and a time
 * window to search, it returns one of four verdicts:
 *
 *   - 'clear': even the shallowest charted sample already clears the safety depth
 *     with no help from the tide. Nothing to gate.
 *   - 'passable-with-tide': foul at low water, but the tide lifts it over the
 *     safety depth for part of the cycle. We return the window(s) when the
 *     effective depth is at or above the safety depth: the times we CAN cross.
 *   - 'hazard': it never clears, even at the highest tide in the window. Too
 *     shallow to cross, full stop.
 *   - 'needs-manual-calc': there's no height-of-tide data for this spot (the fn
 *     returns null across the window), so we can't compute a gate. We say so
 *     plainly rather than inventing a height.
 *
 * The shallowest charted sample governs: if the thinnest spot on the stretch
 * clears, the whole stretch clears. Pure and deterministic (the caller injects
 * the height fn and the window), so it unit-tests without a store, map, or clock.
 */

const MS_PER_MIN = 60_000;
const MS_PER_H = 3_600_000;

/** Look a full tidal day ahead by default, so a gate always falls in the window. */
export const DEFAULT_GATE_WINDOW_H = 24;
/** Sample the height every 15 min: fine enough to place a gate edge within a tap. */
export const DEFAULT_GATE_STEP_MIN = 15;
/** Cap on samples, so even a long window stays cheap; the step widens to fit. */
export const DEFAULT_GATE_MAX_SAMPLES = 200;

/** Height of tide above chart datum at an instant, metres, or null when unknown. */
export type TideHeightAtFn = (ms: number) => number | null;

export type TideGateVerdict = 'clear' | 'passable-with-tide' | 'hazard' | 'needs-manual-calc';

/** One window when the effective depth is at or above the safety depth. */
export interface TideGateWindow {
  /** First moment the gate is open, epoch ms. */
  startMs: number;
  /** Last moment the gate is open, epoch ms. */
  endMs: number;
  /** True when the window was still open at the end of the search horizon, so the
   *  real gate may run on past here (we just stopped looking). */
  openEnded: boolean;
}

export interface TideGateInput {
  /**
   * Charted depth samples along the shallow stretch, metres below datum. May be
   * negative for a drying height (dries above datum). The shallowest sample
   * governs the verdict. An empty array is treated as no depth constraint.
   */
  chartedDepthsM: number[];
  /** Height of tide above datum at a time, metres; null where there's no data. */
  tideHeightAt: TideHeightAtFn;
  /** Effective depth we need over the shallowest spot, metres. */
  safetyDepthM: number;
  /** Start of the search window, epoch ms. */
  windowStartMs: number;
  /** Window length, hours. Default DEFAULT_GATE_WINDOW_H. */
  windowHours?: number;
  /** Sample spacing, minutes. Default DEFAULT_GATE_STEP_MIN. */
  stepMinutes?: number;
  /** Hard cap on samples. Default DEFAULT_GATE_MAX_SAMPLES. */
  maxSamples?: number;
}

export interface TideGateResult {
  verdict: TideGateVerdict;
  /** The shallowest charted depth used (min of the samples), metres below datum. */
  chartedDepthM: number;
  /** The safety depth the assessment used, metres. */
  safetyDepthM: number;
  /**
   * Windows when the effective depth (charted + tide) is at or above the safety
   * depth. Empty for 'hazard' and 'needs-manual-calc'; for 'clear' a single
   * window spanning the whole horizon.
   */
  windows: TideGateWindow[];
  /** Best (deepest) effective depth seen in the window, metres, or null when no
   *  height data was available anywhere in the window. */
  maxEffectiveDepthM: number | null;
  /** How far short of the safety depth the best moment falls, metres, for a
   *  'hazard' (positive = still too shallow at the top of the tide). null else. */
  shortfallM: number | null;
}

interface Sample {
  ms: number;
  /** charted + tide at this instant, or null when the height was unknown. */
  eff: number | null;
}

/** Linear-interpolate the instant the effective depth crosses `level` between two
 *  bracketing valid samples. Both must have a numeric eff. */
function crossMs(a: Sample, b: Sample, level: number): number {
  const ae = a.eff as number;
  const be = b.eff as number;
  const denom = be - ae;
  if (denom === 0) return b.ms;
  const t = (level - ae) / denom;
  return a.ms + t * (b.ms - a.ms);
}

/**
 * Work out the tidal-gate verdict and window(s) for a shallow stretch. See the
 * file header for the four verdicts and how the shallowest sample governs.
 */
export function assessTideGate(input: TideGateInput): TideGateResult {
  const safetyDepthM = input.safetyDepthM;
  // Shallowest charted sample governs. No samples means no depth constraint, so
  // treat it as deep (nothing to gate) rather than inventing a hazard.
  const chartedDepthM = input.chartedDepthsM.length ? Math.min(...input.chartedDepthsM) : Infinity;

  const windowHours = input.windowHours && input.windowHours > 0 ? input.windowHours : DEFAULT_GATE_WINDOW_H;
  const windowMs = windowHours * MS_PER_H;
  const windowEndMs = input.windowStartMs + windowMs;

  // Already deep enough on the chart alone: it clears at any state of tide, so
  // there's no gate to compute. Report a window across the whole horizon.
  if (chartedDepthM >= safetyDepthM) {
    return {
      verdict: 'clear',
      chartedDepthM,
      safetyDepthM,
      windows: [{ startMs: input.windowStartMs, endMs: windowEndMs, openEnded: true }],
      maxEffectiveDepthM: null,
      shortfallM: null,
    };
  }

  const requestedStep = input.stepMinutes && input.stepMinutes > 0 ? input.stepMinutes : DEFAULT_GATE_STEP_MIN;
  const cap = Math.max(2, Math.floor(input.maxSamples ?? DEFAULT_GATE_MAX_SAMPLES));
  let stepMs = requestedStep * MS_PER_MIN;
  let count = Math.floor(windowMs / stepMs) + 1;
  if (count > cap) {
    // Widen the step to land within the cap, keeping the same window.
    stepMs = Math.ceil(windowMs / (cap - 1));
    count = Math.min(cap, Math.floor(windowMs / stepMs) + 1);
  }

  const samples: Sample[] = [];
  let anyHeight = false;
  let maxEff = -Infinity;
  for (let i = 0; i < count; i++) {
    const ms = i === count - 1 ? windowEndMs : input.windowStartMs + i * stepMs;
    const h = input.tideHeightAt(ms);
    if (h == null || !Number.isFinite(h)) {
      samples.push({ ms, eff: null });
      continue;
    }
    anyHeight = true;
    const eff = chartedDepthM + h;
    if (eff > maxEff) maxEff = eff;
    samples.push({ ms, eff });
  }

  // No height reading anywhere in the window: we genuinely can't compute a gate.
  // Say so — do NOT fall through to a false pass or a false hazard.
  if (!anyHeight) {
    return {
      verdict: 'needs-manual-calc',
      chartedDepthM,
      safetyDepthM,
      windows: [],
      maxEffectiveDepthM: null,
      shortfallM: null,
    };
  }

  // Walk the samples, opening a window when the effective depth reaches the safety
  // depth and closing it when it drops back, interpolating each crossing so the
  // gate edges land between samples rather than snapping to the step.
  const isOpen = (eff: number) => eff >= safetyDepthM;
  const windows: TideGateWindow[] = [];
  let openStart: number | null = null;
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i];
    const prev = i > 0 ? samples[i - 1] : null;
    if (s.eff == null) {
      // A data gap: close any open gate at the last known sample; we can't vouch
      // for water we couldn't read.
      if (openStart != null) {
        windows.push({ startMs: openStart, endMs: prev && prev.eff != null ? prev.ms : s.ms, openEnded: false });
        openStart = null;
      }
      continue;
    }
    const open = isOpen(s.eff);
    if (open && openStart == null) {
      openStart =
        prev && prev.eff != null && !isOpen(prev.eff) ? crossMs(prev, s, safetyDepthM) : s.ms;
    } else if (!open && openStart != null) {
      const endMs =
        prev && prev.eff != null && isOpen(prev.eff) ? crossMs(prev, s, safetyDepthM) : s.ms;
      windows.push({ startMs: openStart, endMs, openEnded: false });
      openStart = null;
    }
  }
  if (openStart != null) {
    windows.push({ startMs: openStart, endMs: samples[samples.length - 1].ms, openEnded: true });
  }

  const maxEffectiveDepthM = Number.isFinite(maxEff) ? maxEff : null;

  // Never reaches the safety depth, even at the top of the tide: a real hazard.
  if (windows.length === 0) {
    return {
      verdict: 'hazard',
      chartedDepthM,
      safetyDepthM,
      windows: [],
      maxEffectiveDepthM,
      shortfallM: maxEffectiveDepthM != null ? safetyDepthM - maxEffectiveDepthM : null,
    };
  }

  // Charted depth is below the safety depth but the tide lifts it over for part of
  // the cycle: passable, but only inside the gate.
  return {
    verdict: 'passable-with-tide',
    chartedDepthM,
    safetyDepthM,
    windows,
    maxEffectiveDepthM,
    shortfallM: null,
  };
}
